/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import React, { useState } from 'react';
import classNames from 'classnames';

import { ACCOUNT_SETTINGS_PAGES } from '../../../../constants/accountSettingsPages';
import { FormattedMessage } from '../../../../util/reactIntl';
import { ensureCurrentUser } from '../../../../util/data';

import {
  ExternalLink,
  InlineTextButton,
  NamedLink,
  NotificationBadge,
  Button,
  IconClose,
} from '../../../../components';

import TopbarSearchForm from '../TopbarSearchForm/TopbarSearchForm';
import css from './TopbarMobileMenu.module.css';

const ADMIN_PAGES = [
  'AdminTransactionsPage',
  'AdminTransactionDetailPage',
  'AdminUsersPage',
  'AdminUserDetailPage',
  'AdminAbandonedCartsPage',
  'BlogAdminPage',
  'BlogPostEditorNew',
  'BlogPostEditorEdit',
  'CategoriesAdminPage',
  'CategorySlugsAdminPage',
];

const AdminMobileSection = ({ currentPage, currentPageClass }) => {
  const isAdminPage = ADMIN_PAGES.includes(currentPage);
  const [expanded, setExpanded] = useState(true);

  const linkClass = (...pages) =>
    classNames(css.navigationLink, pages.includes(currentPage) ? css.currentPage : null);

  return (
    <div className={css.adminSection}>
      <button
        type="button"
        className={classNames(css.adminToggle, {
          [css.adminToggleOpen]: expanded,
          [css.adminToggleActive]: isAdminPage,
        })}
        aria-expanded={expanded}
        onClick={() => setExpanded(prev => !prev)}
      >
        <span className={css.adminToggleLabel}>
          <FormattedMessage id="UserNav.admin" />
        </span>
        <span className={css.adminChevron} aria-hidden="true">
          {expanded ? '▾' : '▸'}
        </span>
      </button>
      {expanded ? (
        <div className={css.adminSubLinks}>
          <NamedLink
            className={linkClass('AdminTransactionsPage', 'AdminTransactionDetailPage')}
            name="AdminTransactionsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="UserNav.adminTransactions" />
          </NamedLink>
          <NamedLink
            className={linkClass('AdminUsersPage', 'AdminUserDetailPage')}
            name="AdminUsersPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="UserNav.adminUsers" />
          </NamedLink>
          <NamedLink
            className={linkClass('AdminAbandonedCartsPage')}
            name="AdminAbandonedCartsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="UserNav.adminAbandonedCarts" />
          </NamedLink>
          <NamedLink
            className={linkClass('BlogAdminPage', 'BlogPostEditorNew', 'BlogPostEditorEdit')}
            name="BlogAdminPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="UserNav.contentManagement" />
          </NamedLink>
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('CategoriesAdminPage'))}
            name="CategoriesAdminPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="UserNav.categories" />
          </NamedLink>
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('CategorySlugsAdminPage'))}
            name="CategorySlugsAdminPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="UserNav.categorySlugs" />
          </NamedLink>
        </div>
      ) : null}
    </div>
  );
};

const CustomLinkComponent = ({ linkConfig, currentPage }) => {
  const { group, text, type, href, route } = linkConfig;
  const getCurrentPageClass = page => {
    const hasPageName = name => currentPage?.indexOf(name) === 0;
    const isCMSPage = pageId => hasPageName('CMSPage') && currentPage === `${page}:${pageId}`;
    const isInboxPage = tab => hasPageName('InboxPage') && currentPage === `${page}:${tab}`;
    const isCurrentPage = currentPage === page;

    return isCMSPage(route?.params?.pageId) || isInboxPage(route?.params?.tab) || isCurrentPage
      ? css.currentPage
      : null;
  };

  // Note: if the config contains 'route' keyword,
  // then in-app linking config has been resolved already.
  if (type === 'internal' && route) {
    // Internal link
    const { name, params, to } = route || {};
    const className = classNames(css.navigationLink, getCurrentPageClass(name));
    return (
      <NamedLink name={name} params={params} to={to} className={className}>
        <span className={css.menuItemBorder} />
        {text}
      </NamedLink>
    );
  }
  return (
    <ExternalLink href={href} className={css.navigationLink}>
      <span className={css.menuItemBorder} />
      {text}
    </ExternalLink>
  );
};

/**
 * Menu for mobile layout (opens through hamburger icon)
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.isAuthenticated
 * @param {string?} props.currentPage
 * @param {boolean} props.currentUserHasListings
 * @param {Object?} props.currentUser API entity
 * @param {number} props.notificationCount
 * @param {Array<Object>} props.customLinks Contains object like { group, text, type, href, route }
 * @param {Function} props.onLogout
 * @returns {JSX.Element} search icon
 */
const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentPage,
    inboxTab,
    currentUser,
    notificationCount = 0,
    customLinks,
    onLogout,
    showCreateListingsLink,
    showSearchForm,
    onSearchSubmit,
    initialSearchFormValues,
    intl,
    history,
    location,
    config,
    onCloseMenu,
  } = props;

  const user = ensureCurrentUser(currentUser);

  const extraLinks = customLinks.map((linkConfig, index) => {
    return (
      <CustomLinkComponent
        key={`${linkConfig.text}_${index}`}
        linkConfig={linkConfig}
        currentPage={currentPage}
      />
    );
  });

  // Button "Déposer une annonce" removed from mobile menu
  const createListingsLinkMaybe = null;

  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    const isInboxPage = currentPage?.indexOf('InboxPage') === 0 && page?.indexOf('InboxPage') === 0;
    return currentPage === page || isAccountSettingsPage || isInboxPage ? css.currentPage : null;
  };

  if (!isAuthenticated) {
    const signupOrLogin = (
      <NamedLink name="LoginPage" className={css.signupOrLoginButton}>
        <span className={css.menuItemBorder} />
        <FormattedMessage id="TopbarMobileMenu.signupOrLoginButton" />
      </NamedLink>
    );

    return (
      <nav className={css.root}>
        {/* Close button integrated in menu for non-authenticated users */}
        <Button
          rootClassName={css.closeButton}
          onClick={onCloseMenu}
          title={intl.formatMessage({ id: 'Modal.closeModal' })}
        >
          <IconClose rootClassName={css.closeIcon} />
        </Button>

        {/* Top header with search for non-authenticated users */}
        <div className={css.topHeaderGuest}>
          {/* Integrated search form in mobile menu */}
          {showSearchForm && (
            <div className={css.mobileSearchContainer}>
              <TopbarSearchForm
                rootClassName={css.mobileSearchForm}
                onSubmit={onSearchSubmit}
                initialValues={initialSearchFormValues}
                isMobile={true}
                appConfig={config}
                hidePlaceholder={true}
              />
            </div>
          )}
        </div>

        <div className={css.content}>
          <div className={css.authenticationSection}>{signupOrLogin}</div>

          <div className={css.shopBySection}>
            <div className={css.shopByLabel}>
              <FormattedMessage id="TopbarMobileMenu.shopBy" />
            </div>
            <NamedLink
              className={classNames(css.navigationLink, currentPageClass('BrandsPage'))}
              name="BrandsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.brands.label" />
            </NamedLink>
            <NamedLink
              className={classNames(css.navigationLink, currentPageClass('CategoriesPage'))}
              name="CategoriesPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.categories.label" />
            </NamedLink>
            <NamedLink
              className={classNames(css.navigationLink, currentPageClass('CompatibilityPage'))}
              name="CompatibilityPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.compatibility.label" />
            </NamedLink>
          </div>

          <div className={css.customLinksWrapper}>{extraLinks}</div>

          <div className={css.spacer} />
        </div>
        <div className={css.footer}>{createListingsLinkMaybe}</div>
      </nav>
    );
  }

  const notificationCountBadge =
    notificationCount > 0 ? (
      <NotificationBadge className={css.notificationBadge} count={notificationCount} />
    ) : null;

  const displayName = user.attributes.profile.firstName;
  const manageListingsLinkMaybe = showCreateListingsLink ? (
    <NamedLink
      className={classNames(css.navigationLink, currentPageClass('ManageListingsPage'))}
      name="ManageListingsPage"
    >
      <span className={css.menuItemBorder} />
      <FormattedMessage id="TopbarMobileMenu.yourListingsLink" />
    </NamedLink>
  ) : null;

  const userType = currentUser?.attributes?.profile?.publicData?.userType;
  const isProvider = userType === 'provider';
  const isAdmin = userType === 'admin';

  return (
    <div className={css.root}>
      {/* Close button integrated in menu */}
      <Button
        rootClassName={css.closeButton}
        onClick={onCloseMenu}
        title={intl.formatMessage({ id: 'Modal.closeModal' })}
      >
        <IconClose rootClassName={css.closeIcon} />
      </Button>

      {/* Top header with search */}
      <div className={css.topHeader}>
        {/* Integrated search form in mobile menu */}
        {showSearchForm && (
          <div className={css.mobileSearchContainer}>
            <TopbarSearchForm
              rootClassName={css.mobileSearchForm}
              onSubmit={onSearchSubmit}
              initialValues={initialSearchFormValues}
              isMobile={true}
              appConfig={config}
              hidePlaceholder={true}
            />
          </div>
        )}
      </div>

      <div className={css.content}>
        <span className={css.greeting}>
          <FormattedMessage id="TopbarMobileMenu.greeting" values={{ displayName }} />
        </span>

        <div className={css.shopBySection}>
          <div className={css.shopByLabel}>
            <FormattedMessage id="TopbarMobileMenu.shopBy" />
          </div>
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('BrandsPage'))}
            name="BrandsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.brands.label" />
          </NamedLink>
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('CategoriesPage'))}
            name="CategoriesPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.categories.label" />
          </NamedLink>
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('CompatibilityPage'))}
            name="CompatibilityPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.compatibility.label" />
          </NamedLink>
        </div>

        {/* Profile Section */}
        <div className={css.profileSection}>
          <div className={css.profileLabel}>
            <FormattedMessage id="TopbarMobileMenu.myProfile" />
          </div>
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('ProfileDashboardPage'))}
            name="ProfileDashboardPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarMobileMenu.profileDashboardLink" />
          </NamedLink>
          {!isProvider && (
            <NamedLink
              className={classNames(css.navigationLink, currentPageClass('OrdersPage'))}
              name="OrdersPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarMobileMenu.ordersLink" />
            </NamedLink>
          )}
          {!isProvider && (
            <NamedLink
              className={classNames(css.navigationLink, currentPageClass('CagnotteFidelitePage'))}
              name="CagnotteFidelitePage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="UserNav.cagnotteFidelite" />
            </NamedLink>
          )}
          {isProvider && (
            <NamedLink
              className={classNames(css.navigationLink, currentPageClass('SalesPage'))}
              name="SalesPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="UserNav.sales" />
            </NamedLink>
          )}
          <NamedLink
            className={classNames(
              css.navigationLink,
              currentPageClass('MyRequestsPage'),
              currentPageClass('NewSupportRequestPage'),
              currentPageClass('SupportRequestDetailPage')
            )}
            name="MyRequestsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id={isProvider ? 'UserNav.sellerRequests' : 'UserNav.myRequests'} />
          </NamedLink>
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="UserNav.profileSettings" />
          </NamedLink>
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
            params={{ tab: 'contact-details' }}
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="UserNav.accountSettings" />
          </NamedLink>
          {manageListingsLinkMaybe}
        </div>

        {isAdmin ? (
          <AdminMobileSection currentPage={currentPage} currentPageClass={currentPageClass} />
        ) : null}

        <div className={css.customLinksWrapper}>{extraLinks}</div>
        <div className={css.spacer} />
      </div>
      <div className={css.footer}>
        {createListingsLinkMaybe}
        <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
          <span className={css.menuItemBorder} />
          <FormattedMessage id="TopbarMobileMenu.logoutLink" />
        </InlineTextButton>
      </div>
    </div>
  );
};

export default TopbarMobileMenu;
